import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PageHeader from "../../paragraphHeader";
import { galleryFetch } from "../../../services/api/Gallery";
import ImageModal from "./Model";
import type { GalleryImage } from "../../../types/gallery";

const GalleryPage: React.FC = () => {
  const { folder, slug } = useParams<{
    folder: string;
    slug: string;
  }>();

  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [heading, setHeading] = useState("");

  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchGallery = async () => {
      if (!folder || !slug) {
        setImages([]);
        setHeading("");
        setLoading(false);
        return;
      }

      try {
        const folderName = decodeURIComponent(folder);
        const slugName = decodeURIComponent(slug);

        const response = await galleryFetch(
          `storage-gallery/${folderName}/${slugName}`,
        );

        const apiImages = response?.data?.images || [];

        const headingName =
          response?.data?.folder?.split("/").pop() || slugName;

        setImages(apiImages);
        setHeading(headingName);
      } catch (error) {
        console.error("Gallery fetch error:", error);
        setImages([]);
        setHeading("");
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, [folder, slug]);

  const handleImageClick = (image: GalleryImage, index: number) => {
    setCurrentIndex(index);
    setSelectedImage(image);
  };

  const handleNavigate = (index: number) => {
    if (index < 0 || index >= images.length) return;

    setCurrentIndex(index);
    setSelectedImage(images[index]);
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title={
            <h1 className="font-semibold text-[#1F4D8F] uppercase">Gallery</h1>
          }
          subtitle={
            <>
              Explore{" "}
              <span className="text-blue-500 font-bold capitalize">
                {heading.replace(/[-_]/g, " ")}
              </span>
            </>
          }
          paragraph="Explore Haryana's premier sports events and championships."
          backgroundImage=""
          align="center"
          showOlympicsIcon={true}
        />

        <div className="max-w-7xl mx-auto p-6">
          <div className="mb-10">
            <h2 className="text-3xl font-bold text-slate-800 capitalize">
              {heading ? heading.replace(/[-_]/g, " ") : "Explore Our Gallery"}
            </h2>

            <div className="w-20 h-1 bg-yellow-400 rounded-full mt-3" />
          </div>

          {loading ? (
            <div className="text-center py-20">
              <p>Loading gallery...</p>
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500">No gallery images found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((image, index) => (
                <div
                  key={`${image.url}-${index}`}
                  onClick={() => handleImageClick(image, index)}
                  className="bg-white rounded-xl shadow hover:shadow-xl transition overflow-hidden group cursor-pointer"
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={image.url}
                      alt={image.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ImageModal
        image={selectedImage}
        images={images}
        currentIndex={currentIndex}
        onClose={() => setSelectedImage(null)}
        onNavigate={handleNavigate}
      />
    </>
  );
};

export default GalleryPage;
